import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import imageConversions from "../logic/imageConversion";

/**
 * Uploads and (optionally) resizes an image File to Firebase Storage,
 * returning its download URL.
 *
 * @param {File}   file        — the incoming File
 * @param {string} entityID    — e.g. artist ID, vendor ID, gathering ID
 * @param {string} entityType  — one of "artist", "vendor", "participant",
 *                              "gathering-map", "gathering-logo"
 * @returns {Promise<string|null>}  — the download URL, or null on failure
 */
const uploadImage = async (file, entityID, entityType) => {
  if (!file) return null;

  // decide path + filename
  let baseDirectory, filename;
  switch (entityType.toLowerCase()) {
    case "artist":
      baseDirectory = `artistImages/${entityID}`;
      filename = "artistImage.jpeg";
      break;
    case "vendor":
      baseDirectory = `vendorImages/${entityID}`;
      filename = "vendorImage.jpeg";
      break;
    case "participant":
      baseDirectory = `participantImages/${entityID}`;
      filename = "participantImage.jpeg";
      break;
    case "gathering-map":
      baseDirectory = `gatherings/${entityID}/map`;
      filename = "mapImage.jpeg";
      break;
    case "gathering-logo":
      baseDirectory = `gatherings/${entityID}/logo`;
      filename = "logoImage.jpeg";
      break;
    default:
      throw new Error(`Invalid entity type: ${entityType}`);
  }

  const storage = getStorage();
  const storageRef = ref(storage, `${baseDirectory}/${filename}`);

  try {
    // 1) ensure JPEG
    let fileToUpload = file;
    if (!file.name.match(/\.(jpe?g)$/i)) {
      const dataUrl = await imageConversions.imageToDataURL(fileToUpload);
      fileToUpload = imageConversions.dataURLtoFile(dataUrl, filename);
    }

    // 2) resize if NOT a map image
    if (entityType.toLowerCase() !== "gathering-map") {
      const dataUrl = await imageConversions.imageToDataURL(fileToUpload);
      const MAX_DIM = 1024;          // phones look fine up to ~1K
      const resizedDataUrl = await imageConversions.resizeImageDataURL(
        dataUrl,
        MAX_DIM,
        MAX_DIM,
        0.8
      );
      fileToUpload = imageConversions.dataURLtoFile(resizedDataUrl, filename);
    }

    // 3) upload
    const snapshot = await uploadBytes(storageRef, fileToUpload);
    const url = await getDownloadURL(snapshot.ref);
    console.log(`File uploaded successfully: ${url}`);
    return url;
  } catch (error) {
    console.error("Failed to upload image", error);
    return null;
  }
};

export default uploadImage;
